import {lockr, action, computed, observable, _, React, stringify} from '@api'
import {nav, ui, CatalogStore, GroupByGrouping, DashboardQueryParams, ICatalogFieldConfig, OFFLINE_ID, IGroupOrderBy, VLP_PLAN_GROUP, VLP_FOLDER_GROUP, VLP_COLLECTION_GROUP, IGroupByGroupingLocalStorageSettings, IIndividualGroupSettings, IDragDropElement, DragDropInteraction, GroupByMenuStore, GroupSwatchSettings, LOG_GLOBAL_CATALOG_LOAD, onSiteError, globalStores, shouldIgnoreCatalogField} from '@stores'
import {RootGroupsStore, CatalogItemsCardViewStore, GroupByPivotStore, AggregateDetailsStore, GroupByGroupStore, GroupByGroup_TargetExtra, BuilderCardStore, utils, GroupByDropDownMenuSource, GroupByToolbarChipsStore, DEBUG_CATALOG_ITEM_CARDS} from '@modules'
import {CatalogInstanceChildStore, defaultGroupBySettings, IGroupBySettings} from '@stores/catalog'
import {VerifyDragDropCallback} from '@stores/dragDrop'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {removeUndefinedsNested} from '@utils'
import {Base64} from 'js-base64'
import {reaction, toJS, trace, when} from 'mobx'
import {computedFn, IDisposer} from 'mobx-utils'
import {autorun, runInAction} from '@api'

const labelsForSettings = new Map<keyof IGroupBySettings, string>([
    ['hierarchy', 'Groups']
    // ['columns', 'Grid Columns']
  ]
)

export interface ISettingsForGroupLevel {
  swatchSize?: number
  swatches?: Array<GroupSwatchSettings>
  subtitleFields?: Array<string>
  heroCardField?: string
  twoDColumnView?: boolean
}

/**
 * Fields not to include when writing a preset
 */
const ignoreForPreset = new Set<keyof IGroupBySettings>(['depth','individualGroupSettings', 'expandAllLevel'])

const alwaysIncludeInPreset = new Set<keyof IGroupBySettings>(['hierarchy', 'settingsForLevel'])

export class GroupByStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    synchronizeStores(() => {
      const {vlp, stores, stores: {catalogTab, isGlobalStore}, _toDispose} = this

      if (isGlobalStore) {
        this.initialized = true
      }
      else if (!catalogTab || !stores?.purpose) {
        this.menuStore = new GroupByMenuStore(this)
        _toDispose.push(
          autorun(() => {
            const {groupByFields} = this, {workspaceConfig, workspaces} = catalog

            if (!workspaceConfig || _.isEmpty(workspaces.availableWorkspaces)) {
              this.initialized = false
              return
            }

            const {vlp: {hierarchy: {hasVlpHierarchy, hasLoadedHierarchy}}} = this
            if (hasVlpHierarchy && !hasLoadedHierarchy) {
              this.initialized = false
              return
            }
            // trace()

            //LOG_GLOBAL_CATALOG_LOAD && console.debug(`Initializing group by dictionary from catalog fields`, {hasLoadedHierarchy, groupByFields})
            runInAction(`Initialize product groupings in store due to viewByOptions/customAttributes/tags change`, () => {
              this.hierarchy = this.hierarchy.filter(v => this.groupByFieldsByName[v])
              const {groupingByName, hierarchy} = this

              groupingByName.clear()

              groupingByName.set(OFFLINE_ID, new GroupByGrouping(OFFLINE_ID, this))

              if (workspaceConfig?.catalog?.index) {
                groupByFields.forEach(f => {
                  const key = f.field
                  if (!groupingByName.has(key)) {
                    const group = new GroupByGrouping(key, this)
                    groupingByName.set(key, group)
                  }
                })
                groupingByName.set(workspaceConfig.catalog.index, new GroupByGrouping(workspaceConfig.catalog.index, this))
              }

              this.settings.hierarchy = hierarchy.filter(v => groupingByName.has(v))

              let groupingSettings
              if (isGlobalStore) {
                const {localStorageKey} = this
                groupingSettings = lockr.get<IGroupByGroupingLocalStorageSettings>(localStorageKey)
              } else {
                const {stores: {flexLayoutCatalogRoot: root}} = catalog
                if (root) {
                  groupingSettings = JSON.parse(Base64.decode(root.config?.settings?.groupBy?.groupingSettings ?? '') || '{}')
                }
              }

              if (!_.isEmpty(groupingSettings)) {
                _.keys(groupingSettings).forEach(groupName => {
                  const g = groupingByName.get(groupName)
                  if (g) {
                    g.deserializeGroupSettings(groupingSettings[groupName]?.byGroup ?? {})
                  }
                })
              }

              this.initialized = true
            })
          }, {
            name: `Initialize groups based on current board config`
          }),

          this.autorun_queryGroupByCountsForMenu(),

          autorun(() => {
            const {settings, viewStore} = this
            if (settings.showOnlyExpanded && viewStore?.mgr?.partitionedGroups.length === 0) {
              settings.showOnlyExpanded = false
            }
          }),

          autorun(() => {
            this.settings.flatModeColumns = Math.min(this.settings.flatModeColumns, this.maxChildGroupColumns)
          }),

          reaction(() => this.hierarchy.length, () => {
            this.settings.depth = Math.max(this.settings.depth, this.hierarchy.length)
          }),
          reaction(() => this.settings.depth, () => {
            const {settings, settings: {depth, hierarchy}} = this
            if (!hierarchy) {
              settings.hierarchy = []
            }
            else if (hierarchy.length > depth) {
              settings.hierarchy = hierarchy.slice(0, depth)
            }
          }),

          reaction(() => this.isProductView, (isProductView) => {
            if (isProductView) {
              this.settings.expandAllLevel = 0
              this.settings.settingsForLevel = _.cloneDeep(defaultGroupBySettings.settingsForLevel)
            }
          }),
          reaction(() => catalog.workspaceConfig, config => {
            const {groupByFieldsByName, hierarchy} = this
            this.settings.hierarchy = hierarchy.filter(g => g === OFFLINE_ID || groupByFieldsByName[g] || (vlp.hierarchy?.hasVlpHierarchy && (g === VLP_PLAN_GROUP || g === VLP_COLLECTION_GROUP || g.startsWith(VLP_FOLDER_GROUP))))
          }))
      }
      else {
        console.log(`No need to load group by info `) //- tab is ${tab.component}`, {tab, stores})
        //console.log(this.stores.tab, this.stores._id, this.stores.purpose)
        this.initialized = true
      }
    })
  }

  _toDispose : Array<IDisposer> = []

  dispose = () => {
    this._toDispose.forEach(f => f())
  }
  get vlp() { return this.catalog.vlp }

  @action reset = () => {
    if (this.initialized) {
      this.settings.hierarchy = []
      this.settings.expandAllLevel = 0
    }
  }
  chipsStore: GroupByToolbarChipsStore

  @computed get canShowSwatchesAndHeroCards() {
    const {isGroupedView, settings: {groupView}, catalog} = this
    return !catalog.isShowingAssortment && !(!isGroupedView || (groupView !== 'card' && groupView !== 'flat') || _.isEmpty(catalog.availableSwatchFields))
  }

  @computed get cardModeAvailable() {
    return this.hierarchyGroups.some(h => h?.allowCardMode)
  }

  @computed get hasLoaded() { return this.groupingByName.size > 0}

  @computed get nonDefaultSettings(): Partial<IGroupBySettings> {
    const {settings} = this

    return _.pickBy(settings, (v, k: keyof IGroupBySettings) => alwaysIncludeInPreset.has(k) || (!ignoreForPreset.has(k) && !_.isEqual(settings[k], defaultGroupBySettings[k])))
  }

  @computed get vlpHierarchyGroups() {
    const {hierarchyGroups} = this
    return hierarchyGroups.filter(hg => hg?.vlpType)
  }

  @computed get isGroupedInVlpHierarchy() {
    return !_.isEmpty(this.vlpHierarchyGroups)
  }

  /**
   * DragDrop definition for drag of catalog item on to group by header
   * @constructor
   */
  @computed get dragDrop_moveCatalogItemsBetweenGroupsInteraction(): DragDropInteraction {
    const {catalog: {mutations: {moveCatalogItemBetweenGroups}, dragDrop}, catalog} = this

    if (this.hierarchyGroups.some(hg => hg?.isAllowedToDragDropAssign !== true || hg?.fieldDefinition?.agg === 'exists')) {
      return undefined
    }

    return {
      onDrop: moveCatalogItemBetweenGroups,
      multiple: true,
      verify: (dragItem: IDragDropElement, dragOverItem: IDragDropElement) => {
        if (this.isProductView) return {allowed: false, message: 'Not grouped'}

        const isEditable = dragDrop.verifyCatalogItemIsEditableForDragOverOperation(dragItem, dragOverItem)
        if (!isEditable?.allowed) return isEditable

        if (dragOverItem?.targetExtra) {
          const targetExtra = dragOverItem.targetExtra as GroupByGroup_TargetExtra,
            {store: dragOverGroup, store: {props: {level}}} = targetExtra,
            hierarchyGroups = this.hierarchyGroups.slice(0, level + 1)

          //console.trace(`verify`, {drag: dragItem.id, drop: dragOverItem.id})

          if (hierarchyGroups.every(hg => hg.isDynamicNestedField)) {
            console.log(`All groups are dynamic nested fields`)
            return {allowed: false, message: `Cannot reassign ${hierarchyGroups[0].fieldDefinition.section}`}
          }

          if (!dragOverGroup.props.info.keys) {
            console.log(`!store.props.info.keys`)
            return {allowed: false, message: '!store.props.info.keys'}
          }

          const cardStore = dragItem?.targetExtra?.store as BuilderCardStore
          if (cardStore?.groupByStore) {
            const {groupByStore: dragGroup} = cardStore
            if (dragGroup === dragOverGroup) {
              return {allowed: null, message: 'No-op (same group drag from <-> to)'}
            } // On ourself
            // On an ancestor group?
            if (dragGroup.props.info.keys && _.keys(dragOverGroup.keysIncludingPath).every(k => dragGroup.keysIncludingPath[k] === dragOverGroup.keysIncludingPath[k])) {
              return {allowed: false, message: 'Drop on to an ancestor group'}
            }

            // If our group is not part of our workspace (ie, it's a linked field like coloredMaterial.material) disallow assign by D&D between groups
            if (dragItem.dragType === catalog.index) {
              const {grouping: {associatedWorkspace, fieldDefinition, sectionConfig}} = dragGroup
              if (sectionConfig?.depth > 0) return {allowed: false, message: `Can't reassign nested fields, this field can only be modified in the ${associatedWorkspace.title} workspace`}
            }

            // Unless the item is offline, do not allow drag and drop of items between any places in the merch hierarchy (not supported in C8)
            if (!dragGroup.isUnassigned && this.isGroupedInVlpHierarchy) {
              const {vlpHierarchyGroups} = this

              if (!vlpHierarchyGroups.every(v => dragGroup.keysIncludingPath[v.groupName] === dragOverGroup.keysIncludingPath[v.groupName])) {
                return {  allowed: false, message: 'Cannot move items around VLP hierarchy'}
              }
            }

            // Don't allow D&D between two groups with different secondary names
            const dynamicSectionGroup = hierarchyGroups.find(hg => hg.sectionConfig?.viewOptions?.field)
            if (dynamicSectionGroup) {
              const sectionField = `${dynamicSectionGroup.sectionConfig.path}.${dynamicSectionGroup.sectionConfig.viewOptions.field}`,
                toValue = dragOverGroup.fullPath[sectionField],
                fromValue = cardStore.groupByStore.fullPath[sectionField]

              console.log(`toValue === fromValue`, {toValue, fromValue})
              return {allowed: toValue === fromValue, message: `Must have same value '${fromValue}'`}
            }

            // Make sure that any of the fields we differ on are editable or that we have custom assignment logic
            if (!dragOverGroup.groupDefinition?.onAssign) {
              const nonEditable = _.keys(dragOverGroup.fullPath).filter(k => cardStore.groupByStore.fullPath[k] !== dragOverGroup.fullPath[k] && !catalog.isFieldEditable(k))
              if (!_.isEmpty(nonEditable)) {
                const message = `Cannot drop - ${nonEditable.join(', ')} not editable.`

                return {allowed: false, message}
              }
            }
          }
        }

        return {allowed: true}
      }
    }
  }

  /**
   * This i
   */
  @computed get individualGroupSettings(): Partial<IIndividualGroupSettings> { return this.settings.individualGroupSettings }

  get settings() { return this.catalog.settings.groupBy ?? defaultGroupBySettings}

  groupAggregationStores = observable.map<string, AggregateDetailsStore>({}, {deep: false})

  /**
   * Only return non-default values to reduce what needs to be stored on the query string
   */
  @computed.struct get settingsForQueryParams() {
    const r: Partial<IGroupBySettings> = {},
      {settings, individualGroupSettings} = this

    _.keys(defaultGroupBySettings).forEach(k => {
      if (k != 'individualGroupSettings' && !_.isEqual(settings[k], defaultGroupBySettings[k])) {
        r[k] = settings[k]
      }
    })

    _.keys(individualGroupSettings).forEach(k => {
      if (individualGroupSettings[k] != defaultGroupBySettings.individualGroupSettings[k]) {
        r.individualGroupSettings = Object.assign({}, r.individualGroupSettings, {[k]: individualGroupSettings[k]})
      }
    })

    return r
  }

  /**
   * Settings for the individual groups in the hierarchy
   */
  @computed get groupsOrdersParsed(): { [groupName: string]: Array<IGroupOrderBy> } {
    return {}

    // const {queryParams: {groupBy_orders}} = nav
    // if (!groupBy_orders) return {}
    //
    // return JSON.parse(Base64.decode(groupBy_orders))
  }

  @computed.struct get groupingSettings() {
    const {
      hierarchyGroups,
      catalogItemsGroup
    } = this

    return [...hierarchyGroups.filter(_.identity), catalogItemsGroup].filter(_.identity).reduce((acc, g) => ({...acc, [g.groupName]: toJS(g.settingsNotDefault)}), {})
  }

  @computed.struct get queryParams(): Partial<DashboardQueryParams> {
    const {
      hierarchyGroups,
      catalogItemsGroup,
      isGlobalStore,
      flexLayout: {flexEnabled}
    } = this

    if (isGlobalStore && flexEnabled) return {}

    return {
      groupBy_orders: Base64.encodeURI(stringify([...hierarchyGroups.filter(_.identity), catalogItemsGroup].filter(_.identity).reduce((acc, g) => ({...acc, [g.groupName]: g.orderBy.toJSON()}), {})))
      // Can cause severe issues on reload as all queries are firing at once
      //groupBy_page:          page.toString(),
    } as Partial<DashboardQueryParams>
  }

  get hierarchy() {
    return this.settings.hierarchy ?? []
  }

  set hierarchy(v) {
    this.settings.hierarchy = v
  }

  @computed get maxGroupDepth() {
    const {catalog: {isShowingAssortment, ui: {settings: {maxGroupDepth}}}} = this
    return isShowingAssortment ? Math.min(2, maxGroupDepth) : maxGroupDepth
  }

  @action swapItemsInHierarchy = (from: GroupByGrouping, to: GroupByGrouping) => {
    from && to && utils.swap(this.hierarchy, from.level, to.level)
  }

  pivotStore: GroupByPivotStore
  aggregationDetails: AggregateDetailsStore

  get dropdownMenuStores() { return this.chipsStore.groupByDropdownStores }

  @computed get pivotView() { return this.settings.groupView == 'pivot'}

  @action toggleShowExpandedFirst = () => this.settings.showExpandedFirst = !this.settings.showExpandedFirst

  @observable.ref viewStore: RootGroupsStore
  @observable.ref catalogItemViewStore: CatalogItemsCardViewStore

  @observable initialized: boolean = false

  @computed get firstGroup(): GroupByGrouping { return _.first(this.hierarchyGroups) ?? this.catalogItemsGroup }

  @computed get hierarchyGroups(): Array<GroupByGrouping> {
    const {hierarchy, isProductView, catalogItemsGroup, groupingByName} = this
    return isProductView ? [catalogItemsGroup] : hierarchy.map(v => groupingByName.get(v)).filter(_.identity)
  }

  @computed get deepestExpansionLevel() {
    const {hierarchyGroups} = this
    const r = hierarchyGroups?.findIndex(g => !g?.isCollapsedAll)
    return r == -1 ? hierarchyGroups?.length : r + 1
  }

  @action addGroup = (groupName?: string) => {
    const {hierarchy, groupingByName, maxGroupDepth} = this
    if (hierarchy.length + 1 > maxGroupDepth) {
      throw new Error(`Maximum hierarchy depth allowed is ${maxGroupDepth}`)
    }
    if (groupName) {
      if (hierarchy.indexOf(groupName) != -1) {
        throw new Error(`${groupName} already is in the group by hierarchy`)
      } else {
        hierarchy.push(groupName)
      }
    } else {
      const firstAvailable = Array.from(groupingByName.keys()).find(k => groupingByName.get(k).level == -1)
      firstAvailable && hierarchy.push(firstAvailable)
    }
  }

  /*
    @action toggleExpandAll = ({path, group, groupName} : {path, group, groupName}) => {
      const {groupingByName, hierarchyGroups, hierarchy} = this,
        grouping = groupingByName.get(groupName),
        isExpanded =grouping.isExpanded(group, path)
      if (!isExpanded) {
        grouping.expanded.add(grouping.pathLookup(group, path))
      } else {
        if (grouping.level != -1 && grouping.level != hierarchy.length - 1) {
          const childGroup = hierarchyGroups[level + 1]
          childGroup.expandAllUnder([...path, group])
        }
      }
    }*/

  isExpandedTo = (path: string[] = []) => {
    const {settings: {groupView}, catalog: {groupBy: {hierarchyGroups}}} = this

    if (groupView === 'pivot') return true

    const r = path?.every((p, i) => hierarchyGroups[i]?.isExpanded(p, i > 0 ? path.slice(0, i) : []))
    //console.log('isExpandedTo', path.join(' → '), r)
    return r
  }

  @computed get groupByFieldsByName() {
    return _.keyBy(this.groupByFields, g => g.field)
  }

  @computed get groupByFields(): Array<ICatalogFieldConfig> {
    //trace()
    const {catalog: {vlpDynamicCatalogFields, availableCatalogFields, workspaceConfig}} = this
    return [
      ...availableCatalogFields.filter(f => f.group !== null
                                            && f.c8?.serviceName !== 'getPlanCollectionC8' // Use VLP special hierarchy fields - NOT config group by
                                            && (f.group || ((ui.defaultCatalogFieldBehavior.group) && !shouldIgnoreCatalogField(workspaceConfig, f.field)))),
      ..._.values(vlpDynamicCatalogFields)
    ]// *//*&& (f.group != null || (catalog.isFieldEditable(f.field)))*/) : []
  }

  @computed get groupByAttributeDefs() {
    const {catalog: {attrForEsField}, groupByFields} = this
    return groupByFields.filter(f => f.def).map(({field}) => attrForEsField(field)).filter(_.identity)
  }

  @computed get selectedSpecs() {
    return _.flatMap(Array.from(this.groupingByName.values()).map(({selected}) => Array.from(selected.keys())))
  }

  @computed get isGroupExpandedAll() {
    const {settings: {expandAllLevel}, hierarchyGroups, catalog: {isGroupedView}} = this
    return !isGroupedView || (expandAllLevel == hierarchyGroups.length && hierarchyGroups.every(hg => hg?.isExpandedAll))
  }

  @computed get isExpandedAll(): boolean {
    const {isGroupExpandedAll, catalog: {assortments, assortments: {selectedAssortment}}} = this
    return (!selectedAssortment || assortments.isExpandedAll) && isGroupExpandedAll
  }

  @computed get isGroupCollapsedAll() {
    const {settings: {expandAllLevel}, hierarchyGroups, catalog: {assortments, isGroupedView, assortments: {selectedAssortment}}} = this
    return !isGroupedView || (expandAllLevel == 0 && hierarchyGroups.every(hg => hg?.isCollapsedAll))
  }

  @computed get isCollapsedAll() {
    const {isGroupCollapsedAll, catalog: {assortments, assortments: {selectedAssortment}}} = this
    return (!selectedAssortment || assortments.isCollapsedAll) && isGroupCollapsedAll
  }

  @action collapseAll = (includeChildren = false) => {
    const {isProductView, settings, hierarchyGroups, isGroupCollapsedAll, catalog: {isShowingAssortment, assortments}} = this

    isShowingAssortment && isGroupCollapsedAll && assortments.collapseAll()
    settings.expandAllLevel = Math.max(0, includeChildren ? 0 : settings.expandAllLevel - 1)

    // Collapse every manually expanded item at the level we just left
    if (!isProductView && !includeChildren) {
      hierarchyGroups.slice(settings.expandAllLevel).forEach(hg => {
        const {settingsByGroup: groupSettings} = hg ?? {}
        groupSettings && Array.from(groupSettings.values()).forEach(v => v.expanded = false)
      })
    }

    //
    if (includeChildren) {
      hierarchyGroups.forEach(hg => hg.collapseAll())
    }
  }

  @action expandAll = () => {
    const {isGroupExpandedAll, settings, settings: {expandAllLevel}, hierarchy, groupingByName, catalog: {isShowingAssortment, assortments}} = this

    if (isShowingAssortment && assortments.isCollapsedAll) {
      isShowingAssortment && assortments.expandAll()
    } else {
      settings.expandAllLevel = Math.min(hierarchy.length, expandAllLevel + 1)

      /**
       * Uncomment the code for YFTS-7403
       */
      for (let i = 0; i < hierarchy.length; i++) {
        const {expanded, nodes, setExpanded} = groupingByName.get(hierarchy[i])
        if (expanded.size != nodes.size) {
          Array.from(nodes.keys()).forEach(k => setExpanded(k, true))

          // Collapse any children
          for (let j = i + 1; j < hierarchy.length; j++) {
            const {expanded, setExpanded} = groupingByName.get(hierarchy[j])
            Array.from(expanded.keys()).forEach(k => setExpanded(k, false))
          }

          break
        }
      }
    }
  }

  groupingByName = observable.map<string, GroupByGrouping>({}, {deep: false})

  @computed get allFieldsInCurrentHierarchy() {
    return _.flatMap(this.hierarchyGroups.filter(_.identity).map(hg => [hg.fieldDefinition, ...hg.otherGroupedFields])).filter(_.identity)
  }

  @computed get isGroupedView() { return !this.isProductView}

  @computed get isProductView() {
    return _.isEmpty(this.hierarchy)
  }

  @computed get hasExpandedCatalogItems() {
    return (this.hierarchyGroups || []).filter(_.identity).some(group => Array.from(group.nodes.values())
      .some(({store}) => store?.hasExpandedCatalogItems))
  }

  esFieldFor = computedFn((groupName: string) => {
    return this.catalog.esFieldFor_byField(groupName, true)
  })

  esFieldRootFor = computedFn((groupName: string) => {
    const {def, fieldDefinition} = this.groupingByName.get(groupName)

    return fieldDefinition?.esField ?? def?.name ?? groupName
  })

  @computed get localStorageKey() {
    const {catalog: {workspaces, workspaceConfig, stores: {storeId, deployments: {selectedDeploymentId}}}, hierarchy} = this
    if (!selectedDeploymentId || !workspaceConfig) return null

    return `${workspaces.localStorageKey}-${storeId}-groupBy-${hierarchy.join('-')}`
  }

  @computed get lastGroupByInHierarchy() {
    const {hierarchy, catalog: {index}} = this
    return _.isEmpty(hierarchy) ? index : _.last(hierarchy)
  }

  @computed get lastGroupInHierarchy(): GroupByGrouping {
    return _.last(this.hierarchyGroups)
  }

  @computed get availableGroups() {
    const {hierarchy, catalog: {catalogConfig: {fields}, index}} = this

    return fields.filter(v => (v.esIndex != index && !_.includes(hierarchy, v.field)))
  }

  @action removeFromHierarchy = (groupName: string) => {
    const {settings: {hierarchy}} = this

    if (!_.includes(hierarchy, groupName))
      throw new Error(`${groupName} is not in the hierarchy - cannot remove it`)

    _.remove(hierarchy, f => f == groupName)
  }

  @action removeHierarchyLevel = (level: number) => {
    this.hierarchy.splice(level, 1)
  }

  @computed get catalogItemsGroup() {
    const {catalog: {workspaceConfig}} = this
    return workspaceConfig ? this.groupingByName.get(workspaceConfig?.catalog?.index) : null
  }

  @action togglePivotView = () => {
    const {settings, settings: {groupView}} = this

    settings.groupView = groupView == 'pivot' ? 'card' : 'pivot'
  }

  @computed
  private get countAggs_fieldsByNestedPath() {
    if (!this.hasLoaded) return null

    const {catalog: {sectionConfigFor, workspaceConfig, groupBy: {groupByFields, groupingByName}}} = this

    // Todo - break things up by fields with esNested = true or section with an associated path property
    const gbFields = groupByFields.filter(f => !f.field.startsWith(`VLP_`)),
      fieldsBySection = _.groupBy(gbFields, f => (f.section ? sectionConfigFor(f.section)?.path : null) ?? workspaceConfig._id)

    return fieldsBySection
  }

  @computed
  private get countAggs_fieldsBySection() {
    if (!this.hasLoaded) return null

    const {catalog: {sectionConfigFor, workspaceConfig, groupBy: {groupByFields, groupingByName}}} = this

    const gbFields = groupByFields.filter(f => !f.field.startsWith(`VLP_`)),
      fieldsBySection = _.groupBy(gbFields, f => (f.section ? sectionConfigFor(f.section)?.path : null) ?? workspaceConfig._id)

    return fieldsBySection
  }

  @computed get countAggs() {
    const {countAggs_fieldsBySection: fieldsBySection, catalog: {sectionConfigFor}, groupingByName} = this
    if (!fieldsBySection) return null

    return {
      aggs: {
        ..._.keys(fieldsBySection).reduce((acc, section) => {
          const fields = fieldsBySection[section],
            sectionConfig = sectionConfigFor(section)

          const aggs = fields.reduce((acc, v) => {
            const {agg} = v,
              field = groupingByName.get(v.field).esField

            return (
              {
                ...acc,
                [v.field]: agg == 'exists' ? {
                  filter: {bool: {filter: []}}, aggs: {items: {filter: {exists: {field}}}}
                } : {cardinality: {field}}
              })
          }, {})

          return _.assign(
            acc,
            sectionConfig?.path ? {
              [sectionConfig.path]: {
                nested: {
                  path: sectionConfig.path
                },
                aggs
              }
            } : aggs)
        }, {})
      }
    }
  }

  @computed get countsSearchClause() {
    const {catalog} = this
    return removeUndefinedsNested(
      {
        size: 0,
        ...catalog.es.catalogItemFilter.filter,
        ...this.countAggs
      })
  }

  @action queryGroupByCountsForMenu = async () => {
    try {
      const {countsSearchClause, es} = this

      // console.log(`Loading GroupByStore.queryCounts...`)
      const {aggregations} = await es.search({
        meta: `GroupByStore.queryCounts`,
        body: countsSearchClause,
        index: this.catalog.index
      }, {log: {query: false, result: false}})

      // Todo - these counts are missing Pending Items, ideally we would add in the offline values as well
      this.counts = aggregations

      if (DEBUG_CATALOG_ITEM_CARDS && _.values(aggregations).every(v => v.value === 0)) debugger
      //console.log(toJS(this.counts))
    } catch (err) {
      onSiteError(err)
    }
  }

  /**
   * Expensive - don't do this until the catalog has finished loading at least once and even then wait a little bit before doing so
   **/
  private autorun_queryGroupByCountsForMenu = () => autorun(
    async () => {
      const {catalog, catalog: {stores, index, es: {catalogItemFilter, refetchId}, groupBy: {groupByFields}}} = this

      // Don't query counts for non-catalog tabs
      if (stores.purpose) return

      // console.log(`autorun_queryGroupByCountsForMenu - ${index}`)

      if (catalog.loading || !groupByFields?.length || !catalogItemFilter || !this.hasLoaded) {
        // console.log(`Skipping counts query`, {loading: catalog.loading, length: groupByFields?.length, filter: catalogItemFilter, hasLoaded: this.hasLoaded})
        return
      }
      const {countsSearchClause} = this

      await this.queryGroupByCountsForMenu()
    },
    {
      name: `Update counts in group by menu`,
      delay: 500 /* Otherwise we trigger repeatedly during filter load */
    })

  @observable.ref counts: { [field: string]: { value?: number, doc_count?: number, items?: { doc_count: number } } } = {}

  getTitleWithCount = computedFn((fieldName: string) => {
    // const fieldCount: any = counts[f.field]
    // const count = groups[f.field]?.selected?.size ||
    //               (group?.fieldDefinition?.agg == 'exists' ? (fieldCount?.doc_count == 0 ? 0 : ((fieldCount?.doc_count - fieldCount?.items?.doc_count) % fieldCount?.doc_count == 0) ? 1 : 2) : (counts && fieldCount?.value)) || 0

    const {catalog: {catalogItemName, labelForField, sectionConfigFor, groupBy: {groupingByName}, filter: {state: {groups}}}} = this,
      {counts} = this,
      g = groupingByName.get(fieldName),
      {fieldDefinition} = g,
      sectionConfig = sectionConfigFor(fieldDefinition?.section),
      label = g.isCatalogItemsGrouping ? catalogItemName : labelForField(fieldDefinition),
      fieldCount = _.isEmpty(counts) ? null : sectionConfig?.path && counts[sectionConfig.path] ? counts[sectionConfig.path][fieldName] : counts[fieldName],
      count = fieldCount === null ? null
                                  : fieldName === OFFLINE_ID
                                    ? 2
                                    : groups[fieldName]?.selected?.size || (fieldDefinition?.agg == 'exists'
                                                                            ? (!fieldCount?.doc_count ? 0 : ((fieldCount?.doc_count - fieldCount?.items?.doc_count) % fieldCount?.doc_count == 0) ? 1 : 2) :
                                                                            (!_.isEmpty(counts) && fieldCount?.value)) || 0

    // console.log({fieldName, count, label})
    return {label, count}
  }, {name: 'getTitleWithCount'})

  @observable loading = false

  @action onClickGroupBy_modifyHierarchy = (group: string, level?: number, source: GroupByDropDownMenuSource = 'toolbar') => {
    const {settings, hierarchy, isProductView, maxGroupDepth, settings: {depth}} = this

    if (group === this.catalog.index) {
      this.hierarchy = []//hierarchy.slice(0, level)
      return
    }

    const groupIndexInHierarchy = hierarchy.indexOf(group)  // Will be -1 if not

    if (!group) {
      settings.hierarchy = []
    } else if (groupIndexInHierarchy == -1 || isProductView) { //
      if (isProductView || (level === -1 && hierarchy.length < maxGroupDepth)) { // Adding a new group to the end of the hierarchy
        hierarchy.push(group)
        if (isProductView) { this.loading = true }
      } else {
        // Replacing an existing group
        if (_.isNumber(level)) {
          hierarchy[level] = group
        } else {
          hierarchy.splice(hierarchy.length - 1, 1, group)
        }
      }
    } else {
      hierarchy.splice(groupIndexInHierarchy, 1)
      if (groupIndexInHierarchy == 0) {
        this.loading = true
      }
    }

    if (hierarchy?.length > maxGroupDepth) {
      hierarchy.splice(1, 1)
    }
  }

  @computed get isGroupBy() {
    return this?.hierarchy?.length
  }

  onChangeColumns = (e, c: number) => {
    this.catalog.settings.groupBy.flatModeColumns = c
  }

  @action onClickLessColumns = () => {
    this.catalog.settings.groupBy.flatModeColumns = Math.max(1, this.catalog.settings.groupBy.flatModeColumns - 1)
  }

  @computed get maxChildGroupColumns() {
    const {catalog: {isShowingAssortment, ui: {settings: {maxChildGroupColumns}}}} = this
    return isShowingAssortment ? Math.min(maxChildGroupColumns, 2) : maxChildGroupColumns
  }

  @action onClickMoreColumns = () => {
    this.catalog.settings.groupBy.flatModeColumns = Math.min(this.maxChildGroupColumns, this.catalog.settings.groupBy.flatModeColumns + 1)
  }

  /**
   * Don't allow dragging from a library on to a group of a different value in order to create new items (Material library -> material group)
   * @param dragItem
   * @param dragOverItem
   */
  verifyDragLibraryIsDifferentThanDropGroupHeader: VerifyDragDropCallback = (dragItem: IDragDropElement, dragOverItem: IDragDropElement) => {
    if (!dragOverItem) { return {allowed: true}}

    const store: GroupByGroupStore = dragOverItem.targetExtra.store,
      assignTo = this.catalog.libraryByIndex[dragItem.dragType]?.assignTo

    if (assignTo) {
      if (_.has(store.keysIncludingPath, assignTo)) {
        return {allowed: false}
      }
    }

    return {allowed: true}
  }

  @action toggleShowHeroCards = () => this.settings.heroCards.show = !this.settings.heroCards.show
  @action toggleHideHeroCardsInCollapsedGroups = () => this.settings.heroCards.hideCollapsed = !this.settings.heroCards.hideCollapsed
  @action toggleCardSwatches = () => this.settings.showSwatches = !this.settings.showSwatches

  @action toggleHideCatalogItemsOnSwatchHover = () => this.settings.hideCatalogItemsOnSwatchHover = !this.settings.hideCatalogItemsOnSwatchHover
  @action toggleShowSwatchesWhenCollapsed = () => this.settings.showSwatchesWhenCollapsed = !this.settings.showSwatchesWhenCollapsed
  @action clearHierarchy = () => {
    this.settings.hierarchy = []
    _.values(this.settings.settingsForLevel).forEach(v => {
      v.subtitleFields = []
    })
  }

  @action increaseDepth = () => this.settings.depth++

  get depth() { return this.settings.depth }

  @observable.ref menuStore: GroupByMenuStore


  groupingForField = (field: ICatalogFieldConfig) => {
    return this.groupingByName.get(field?.field)
  }

  labelForSetting = (k: keyof IGroupBySettings & string) => {
    return labelsForSettings.get(k) ?? utils.toCapitalizedWords(k)
  }

  onSetDepth = (depth: number) => e => {
    runInAction(() => {
      const {settings, hierarchy, chipsStore} = this

      settings.depth = depth
      if (depth > this.hierarchy.length) {
        chipsStore.selectedIndex = -1
      } else {
        chipsStore.selectedIndex = Math.max(depth - 1, 0)
      }
    })
  }
}


// WEBPACK FOOTER //
// ./src/stores/catalog/groupBy/GroupByStore.tsx